import {
  toAnswerOption,
  type AnswerOption,
} from '@/modules/simulado/domain/entities/answer.entity';
import { useState } from 'react';
import type { SimuladoSourceQuestion } from '@/modules/simulado/domain/entities/question.entity';
import { Markdown } from '@/shared/rich-text/Markdown';
import { RichText } from '@/shared/rich-text/RichText';
import { toParagraphs } from '@/shared/rich-text/rich-text';
import { ChevronDown } from '@/shared/ui/Icon';

type Outcome = 'right' | 'wrong' | 'blank' | 'annulled' | 'pending';

const OUTCOME_LABEL: Record<Outcome, string> = {
  right: 'Acertou',
  wrong: 'Errou',
  blank: 'Em branco',
  annulled: 'Anulada',
  pending: 'Resposta oficial ainda não publicada',
};

function outcomeOf(
  question: SimuladoSourceQuestion,
  picked: AnswerOption | undefined,
): Outcome {
  if (question.isAnnulled || question.correctChoice === -1) return 'annulled';
  if (typeof question.correctChoice !== 'number') return 'pending';
  if (!picked) return 'blank';
  return picked === toAnswerOption(question.correctChoice) ? 'right' : 'wrong';
}

/**
 * Uma questão já corrigida: o enunciado, as alternativas com a certa e a
 * marcada, o resultado por extenso e o comentário. É o card da aba "Questões"
 * do `enamed-feedback-lp`, servindo aqui a duas abas — as questões do ensaio
 * e a prova oficial.
 *
 * O resultado vai em texto ao lado do número, e não só na cor da borda: cor
 * sozinha não chega a quem não a enxerga.
 *
 * **Texto como no ENAMED:** enunciado e alternativas podem vir em HTML do banco
 * da Aristo e trazer imagem embutida (`RichText`, `toParagraphs`); o
 * comentário oficial vem em **markdown** (`Markdown`), atrás do botão "Ver
 * comentário oficial Aristo". Nada disso injeta HTML na página.
 */
export function ReviewQuestionCard({
  question,
  picked,
}: {
  question: SimuladoSourceQuestion;
  picked: AnswerOption | undefined;
}) {
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const outcome = outcomeOf(question, picked);
  const correct =
    outcome === 'annulled' || typeof question.correctChoice !== 'number'
      ? null
      : toAnswerOption(question.correctChoice);

  return (
    <article
      className={`qcard is-${outcome}`}
      aria-label={`Questão ${question.number}: ${OUTCOME_LABEL[outcome]}`}
    >
      <div className="qcard-head">
        <h3 className="qcard-number">Questão {question.number}</h3>
        <span className={`qcard-outcome is-${outcome}`}>
          {OUTCOME_LABEL[outcome]}
        </span>
      </div>
      {question.area && <p className="qcard-area">{question.area}</p>}

      {/* Só o enunciado, como no ENAMED: o `complement` da Aristo traz dado de
          controle ("Caderno Único", um número solto), e não texto da questão. */}
      <RichText
        value={question.body}
        className="qcard-body"
        questionNumber={question.number}
      />

      <ul className="qcard-alts">
        {question.choices.map((text, index) => {
          const letter = toAnswerOption(index);
          if (!letter) return null;
          const isCorrect = letter === correct;
          const isPicked = letter === picked;

          return (
            <li
              key={letter}
              className={[
                'alt is-static',
                isCorrect ? 'is-right' : '',
                isPicked && !isCorrect ? 'is-wrong' : '',
              ].join(' ')}
            >
              <span className="alt-letter" aria-hidden="true">
                {letter}
              </span>
              <span className="alt-text">{toParagraphs(text).join(' ')}</span>
              {(isCorrect || isPicked) && (
                <span className="alt-mark">
                  {isCorrect && isPicked
                    ? 'Sua resposta · correta'
                    : isCorrect
                      ? 'Correta'
                      : 'Sua resposta'}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {question.comment && (
        <>
          <button
            type="button"
            className="qcard-comment-toggle"
            onClick={() => setIsCommentOpen((open) => !open)}
            aria-expanded={isCommentOpen}
          >
            <ChevronDown
              size="sm"
              className={isCommentOpen ? 'is-open' : undefined}
            />
            {isCommentOpen
              ? 'Ocultar comentário'
              : 'Ver comentário oficial Aristo'}
          </button>
          {isCommentOpen && (
            // O comentário oficial vem em markdown — ver `Markdown`.
            <Markdown source={question.comment} className="qcard-comment" />
          )}
        </>
      )}
    </article>
  );
}
